import * as Yup from "yup";
import * as uuid from "uuid";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useState } from "react";
import { Auth } from "aws-amplify";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import AppLink from "../../../../components/elements/AppLink";
import BaseInput from "../../../../components/elements/BaseInput";
import Button from "../../../../components/elements/Button";
import FormError from "../../../../components/elements/FormError";
import FormFieldError from "../../../../components/elements/FormFieldError";
import classes from "./SignUpForm.module.scss";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { createAgency } from "../../../../redux/reducers/agency";
import { setIsAuthLoading } from "../../../../redux/reducers/session";
import { successToaster } from "../../../../helpers/toasters";

type FormInitialValues = {
  userName: string;
  companyName: string;
  email: string;
  password: string;
  passwordConfirm: string;
};

const SignUpForm: FC = () => {
  const formInitialValues: FormInitialValues = {
    userName: "",
    companyName: "",
    email: "",
    password: "",
    passwordConfirm: "",
  };

  const validationSchema = Yup.object({
    userName: Yup.string().required("Required field."),
    companyName: Yup.string().required("Required field."),
    email: Yup.string().email("Wrong format, enter valid email").required("Required field."),
    password: Yup.string().required("Required field.").min(8, "Password is too short - should be 8 chars minimum."),
    passwordConfirm: Yup.string().oneOf([Yup.ref("password"), null], "Passwords must match"),
  });

  const history = useHistory();

  const [authErrorMessage, setAuthErrorMessage] = useState("");

  const dispatch = useDispatch();

  const isAuthLoading = useSelector((state: ReduxApplicationState) => state.session.isLoading);

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      const { userName, password, email, companyName } = values;
      const lowerCaseUserName = userName.toLowerCase();

      const agencyToCreate = {
        agencyId: `bgAgency-${uuid.v4()}`,
        createdBy: lowerCaseUserName,
        email,
        name: companyName,
        users: [lowerCaseUserName],
      };

      dispatch(setIsAuthLoading(true));
      await Auth.signUp({ username: lowerCaseUserName, password, attributes: { email } });
      dispatch(createAgency(agencyToCreate));
      dispatch(setIsAuthLoading(false));
      actions.resetForm();
      successToaster(`Confirmation code was successfully sent to your email!`);
      history.push({ search: new URLSearchParams({ userName: lowerCaseUserName }).toString() });
    } catch (error) {
      const err = error as any;
      dispatch(setIsAuthLoading(false));
      actions.resetForm();
      setAuthErrorMessage(`There was an error logging in: ${err.message}`);
    }
  };

  const handleFieldChange =
    (formikHandler: (e: string | ChangeEvent<any>) => void) => (e: string | ChangeEvent<any>) => {
      formikHandler(e);
      if (authErrorMessage) {
        setAuthErrorMessage("");
      }
    };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
    >
      {({ handleChange, values }) => (
        <Form className={classes.container}>
          <div className={classes.formContent}>
            <div className={classes.formHeader}>
              <Typography variant="h3">Create Account</Typography>
              <AccountCircleIcon className={classes.icon} />
            </div>

            <div className={classes.inputs}>
              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.userName}
                  error={!!authErrorMessage}
                  name="userName"
                  variant="outlined"
                  label="Username *"
                  onChange={handleFieldChange(handleChange("userName"))}
                />

                <FormFieldError name="userName" />
              </div>

              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.email}
                  error={!!authErrorMessage}
                  name="email"
                  variant="outlined"
                  label="Email *"
                  onChange={handleFieldChange(handleChange("email"))}
                />

                <FormFieldError name="email" />
              </div>

              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.companyName}
                  error={!!authErrorMessage}
                  name="companyName"
                  variant="outlined"
                  label="Company/Agency Name *"
                  onChange={handleFieldChange(handleChange("companyName"))}
                />

                <FormFieldError name="companyName" />
              </div>

              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.password}
                  error={!!authErrorMessage}
                  name="password"
                  variant="outlined"
                  label="Password *"
                  onChange={handleFieldChange(handleChange("password"))}
                  type="password"
                />

                <FormFieldError name="password" />
              </div>

              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.passwordConfirm}
                  error={!!authErrorMessage}
                  name="passwordConfirm"
                  variant="outlined"
                  label="Confirm Password *"
                  onChange={handleFieldChange(handleChange("passwordConfirm"))}
                  type="password"
                />

                <FormFieldError name="passwordConfirm" />
                <FormError className={classes.formError} errorMessage={authErrorMessage} />
              </div>
            </div>

            <div className={classes.formControls}>
              <Button variant="contained" type="submit" isLoading={isAuthLoading} fullWidth>
                Create Account
              </Button>
              <span>
                <AppLink to="/">Already have an account?</AppLink>{" "}
                <AppLink to="/" highlighted>
                  Log in
                </AppLink>
              </span>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SignUpForm;
